import { gql, useMutation, useQuery } from '@apollo/client';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { StatusBadge } from '../components/coreComponents/badge';
import { Button } from '../components/coreComponents/button';
import { Input } from '../components/coreComponents/input';
import { SelectInput, SelectOption } from '../components/coreComponents/selectInput';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';
import { NavTabs } from '../components/navTabs';
import { AdminRole, AdminUserStatus } from '../graphql/types';
import { useIdentity } from '../identityContext';

interface AdminUserDetailData {
  readonly id: string;
  readonly email: string;
  readonly firstName?: string | null;
  readonly lastName?: string | null;
  readonly role: AdminRole;
  readonly status: AdminUserStatus;
  readonly credentialsCreatedAt?: string | null;
  readonly createdAt: string;
  readonly pendingCredentialsRequest?: {
    readonly id: string;
    readonly kind: string;
    readonly status: string;
    readonly expiresAt: string;
    readonly initiatedByEmail: string;
  } | null;
}

export function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(query, {
    skip: id == null,
    variables: { id: id ?? '' },
    fetchPolicy: 'cache-and-network'
  });

  if (id == null) return <Typography>Administrator not found</Typography>;
  if (loading && data == null) return <Typography>Loading…</Typography>;
  if (error) return <Typography color="error">Error: {error.message}</Typography>;

  const admin: AdminUserDetailData | null = data?.adminUserOne ?? null;
  if (admin == null) return <Typography>Administrator not found</Typography>;

  const name = `${admin.firstName ?? ''} ${admin.lastName ?? ''}`.trim();

  return (
    <MainWrapper>
      <MainWrapperHeader
        title={name || admin.email}
        description={admin.email}
        onBack={() => navigate('/administrators')}
        badge={<AdminStatusBadge status={admin.status} />}
      />

      <NavTabs
        tabs={[
          { value: 'overview', label: 'Overview', node: <OverviewTab admin={admin} /> },
          { value: 'credentials', label: 'Credentials', node: <CredentialsTab admin={admin} /> }
        ]}
      />
    </MainWrapper>
  );
}

function AdminStatusBadge({ status }: { status: AdminUserStatus }) {
  const tone = status === 'active' ? 'success' : status === 'revoked' ? 'error' : 'default';
  return <StatusBadge tone={tone} label={status} isUppercase />;
}

const roleOptions: ReadonlyArray<SelectOption<AdminRole>> = [
  { label: 'Admin', value: 'admin' },
  { label: 'Superadmin', value: 'superAdmin' }
];

function OverviewTab({ admin }: { admin: AdminUserDetailData }) {
  const identity = useIdentity();
  const [update] = useMutation(updateMutation);

  const isDraft = admin.status === 'draft';
  const isSuper = identity.role === 'superAdmin';
  const isSelf = identity.id === admin.id;

  // email locked once credentials exist; role superAdmin-only; name editable
  // while draft, by a superAdmin, or by the admin on their own row.
  const canEditEmail = isDraft;
  const canEditRole = isSuper;
  const canEditName = isDraft || isSuper || isSelf;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: admin.firstName ?? '',
      lastName: admin.lastName ?? '',
      email: admin.email,
      role: admin.role
    },
    validate: values => {
      const errors: Record<string, string> = {};
      if (!values.firstName.trim()) errors.firstName = 'First name is required';
      if (!values.lastName.trim()) errors.lastName = 'Last name is required';
      if (canEditEmail) {
        if (!values.email.trim()) errors.email = 'Email is required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
          errors.email = 'Enter a valid email address';
      }
      return errors;
    },
    onSubmit: async (values, helpers) => {
      try {
        await update({
          variables: {
            input: {
              id: admin.id,
              ...(canEditName ? { firstName: values.firstName.trim() } : {}),
              ...(canEditName ? { lastName: values.lastName.trim() } : {}),
              ...(canEditEmail ? { email: values.email.trim().toLowerCase() } : {}),
              ...(canEditRole ? { role: values.role } : {})
            }
          },
          refetchQueries: ['AdminUserOne', 'AdminUserMany', 'AdminIdentity']
        });
        helpers.setStatus({ ok: true });
      } catch (err: any) {
        helpers.setStatus({ error: err.message ?? 'Failed to save' });
      }
    }
  });

  const status = formik.status as { ok?: boolean; error?: string } | undefined;

  return (
    <Box sx={{ maxWidth: 520 }}>
      <Stack spacing={5}>
        <Input
          label="First name"
          name="firstName"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.firstName && formik.errors.firstName
              ? formik.errors.firstName
              : undefined
          }
          disabled={!canEditName || formik.isSubmitting}
        />
        <Input
          label="Last name"
          name="lastName"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.lastName && formik.errors.lastName ? formik.errors.lastName : undefined
          }
          disabled={!canEditName || formik.isSubmitting}
        />
        <Input
          label="Email"
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.email && formik.errors.email ? formik.errors.email : undefined}
          helperText={
            !canEditEmail ? 'Email is locked once login credentials are created.' : undefined
          }
          disabled={!canEditEmail || formik.isSubmitting}
        />
        <SelectInput
          label="Role"
          value={formik.values.role}
          onChange={value => formik.setFieldValue('role', value)}
          options={roleOptions}
          disabled={!canEditRole || formik.isSubmitting}
          helperText={!canEditRole ? 'Only a superadmin can change the role.' : undefined}
        />

        {status?.error != null && <Alert severity="error">{status.error}</Alert>}
        {status?.ok === true && <Alert severity="success">Saved.</Alert>}

        <Box>
          <Button
            variant="primary"
            onClick={() => formik.handleSubmit()}
            disabled={formik.isSubmitting || !formik.dirty}
          >
            {formik.isSubmitting ? 'Saving…' : 'Save changes'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

function CredentialsTab({ admin }: { admin: AdminUserDetailData }) {
  const identity = useIdentity();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createRequest, { loading: requesting }] = useMutation(createRequestMutation, {
    refetchQueries: ['AdminUserOne']
  });
  const [revokeRequest, { loading: revoking }] = useMutation(revokeRequestMutation, {
    refetchQueries: ['AdminUserOne']
  });
  const [cancelRequest, { loading: cancelling }] = useMutation(cancelRequestMutation, {
    refetchQueries: ['AdminUserOne']
  });
  const [resendRequest, { loading: resending }] = useMutation(resendRequestMutation, {
    refetchQueries: ['AdminUserOne']
  });

  const pending = admin.pendingCredentialsRequest ?? null;
  const isSelf = identity.id === admin.id;

  const run = async (fn: () => Promise<unknown>) => {
    setErrorMessage(null);
    try {
      await fn();
    } catch (err: any) {
      setErrorMessage(err.message ?? 'Something went wrong');
    }
  };

  // An open grant/revoke request — show its state + cancel/resend.
  if (pending != null) {
    const isRevoke = pending.kind === 'adminCredentialsRevoke';
    return (
      <Box sx={{ maxWidth: 640 }}>
        <Stack spacing={3}>
          <Alert severity={isRevoke ? 'warning' : 'info'}>
            A request to {isRevoke ? 'revoke' : 'grant'} login access is awaiting confirmation by a
            second administrator (requested by {pending.initiatedByEmail}; expires{' '}
            {new Date(pending.expiresAt).toLocaleString()}). It must be confirmed from the Pending
            actions page.
          </Alert>
          {errorMessage != null && <Alert severity="error">{errorMessage}</Alert>}
          <Stack direction="row" spacing={3}>
            <Button
              variant="secondary"
              disabled={resending}
              onClick={() => run(() => resendRequest({ variables: { id: pending.id } }))}
            >
              {resending ? 'Resending…' : 'Resend email'}
            </Button>
            <Button
              variant="destructive"
              disabled={cancelling}
              onClick={() => run(() => cancelRequest({ variables: { id: pending.id } }))}
            >
              {cancelling ? 'Cancelling…' : 'Cancel request'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // Active — can sign in; offer revoke.
  if (admin.status === 'active') {
    return (
      <Box sx={{ maxWidth: 640 }}>
        <Stack spacing={3}>
          <Alert severity="success">
            Login access is active
            {admin.credentialsCreatedAt != null
              ? ` since ${new Date(admin.credentialsCreatedAt).toLocaleString()}`
              : ''}
            . This administrator can sign in to the admin app.
          </Alert>
          {errorMessage != null && <Alert severity="error">{errorMessage}</Alert>}
          <Box>
            <Button
              variant="destructive"
              disabled={revoking || isSelf}
              onClick={() => run(() => revokeRequest({ variables: { adminUserId: admin.id } }))}
            >
              {revoking ? 'Requesting…' : 'Revoke access'}
            </Button>
          </Box>
          <Alert severity="info">
            {isSelf
              ? 'You cannot revoke your own access.'
              : 'Revoking starts a two-person approval. Once confirmed, this administrator can no longer sign in to the admin app (their citadel credentials are left intact).'}
          </Alert>
        </Stack>
      </Box>
    );
  }

  // Draft (never granted) or revoked — offer to (re-)grant.
  const isRevoked = admin.status === 'revoked';
  return (
    <Box sx={{ maxWidth: 640 }}>
      <Stack spacing={3}>
        <Alert severity="warning">
          {isRevoked
            ? 'This administrator’s access was revoked and they cannot sign in. Re-granting starts a two-person approval.'
            : 'This administrator has no login credentials yet and cannot sign in. Requesting credentials starts a two-person approval; once a second administrator confirms it, credentials are provisioned and a sign-in email is sent.'}
        </Alert>
        {errorMessage != null && <Alert severity="error">{errorMessage}</Alert>}
        <Box>
          <Button
            variant="primary"
            disabled={requesting}
            onClick={() => run(() => createRequest({ variables: { adminUserId: admin.id } }))}
          >
            {requesting ? 'Requesting…' : isRevoked ? 'Re-grant access' : 'Create credentials'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

const query = gql<AdminUserOneGQL>`
  query AdminUserOne($id: ID!) {
    adminUserOne(id: $id) {
      id
      email
      firstName
      lastName
      role
      status
      credentialsCreatedAt
      createdAt
      pendingCredentialsRequest {
        id
        kind
        status
        expiresAt
        initiatedByEmail
      }
    }
  }
`;

const updateMutation = gql<AdminUserUpdateGQL>`
  mutation AdminUserUpdate($input: AdminUserUpdateInput!) {
    adminUserUpdate(input: $input) {
      id
    }
  }
`;

const createRequestMutation = gql<AdminCredentialsCreateRequestGQL>`
  mutation AdminCredentialsCreateRequest($adminUserId: ID!) {
    adminCredentialsCreateRequest(adminUserId: $adminUserId) {
      id
    }
  }
`;

const revokeRequestMutation = gql<AdminCredentialsRevokeRequestGQL>`
  mutation AdminCredentialsRevokeRequest($adminUserId: ID!) {
    adminCredentialsRevokeRequest(adminUserId: $adminUserId) {
      id
    }
  }
`;

const cancelRequestMutation = gql<AdminCredentialsRequestCancelGQL>`
  mutation AdminCredentialsRequestCancel($id: ID!) {
    adminCredentialsRequestCancel(id: $id) {
      id
    }
  }
`;

const resendRequestMutation = gql<AdminCredentialsRequestResendGQL>`
  mutation AdminCredentialsRequestResend($id: ID!) {
    adminCredentialsRequestResend(id: $id) {
      id
    }
  }
`;
